import React, { createContext, useContext, useMemo } from 'react';

import invariant from 'tiny-invariant';

import { componentWithFG } from '@atlaskit/platform-feature-flags-react';

import type { UserMenuDataShapes } from '../../../common/types';
import { buildUserMenuDataShapes as buildUserMenuDataShapesNEW } from '../../../common/utils/build-user-menu-data-shapes';
import { buildUserMenuDataShapes as buildUserMenuDataShapesOLD } from '../../../common/utils/build-user-menu-data-shapes-OLD';
import { useItemsAndShortcuts } from '../../items-and-shortcuts-provider';
import { useOptions } from '../../options-provider';

import type { UserMenuDataShapesProviderProps } from './types';

export const UserMenuDataShapesContext = createContext<UserMenuDataShapes | undefined>(undefined);

export const UserMenuDataShapesProvider = componentWithFG(
	'navx-1527-fix-foryou-position',
	UserMenuDataShapesProviderNEW,
	UserMenuDataShapesProviderOLD,
);

/**
 * Stores the repaired user menu data, in a variety of shapes, and makes it available to child components.
 * This component exists so that it is easy for child components to read and update the user menu data independently of the user side nav data.
 */
function UserMenuDataShapesProviderNEW({
	children,
	rawUserMenuData,
	setUserMenuData,
}: UserMenuDataShapesProviderProps) {
	const { disableMenuCustomization } = useOptions();
	const { allItems, forYouMenuId, globalItems, shortcuts } = useItemsAndShortcuts();

	const userMenuDataShapes = useMemo(
		(): UserMenuDataShapes =>
			buildUserMenuDataShapesNEW({
				allItemMenuIds: Object.keys(allItems),
				allShortcutMenuIds: Object.keys(shortcuts ?? {}),
				forYouMenuId,
				globalItems,
				// All menu items are visible when there is no user menu data
				rawUserMenuData: disableMenuCustomization ? [] : rawUserMenuData,
				setUserMenuData,
			}),
		[
			allItems,
			disableMenuCustomization,
			forYouMenuId,
			globalItems,
			rawUserMenuData,
			setUserMenuData,
			shortcuts,
		],
	);

	return (
		<UserMenuDataShapesContext.Provider value={userMenuDataShapes}>
			{children}
		</UserMenuDataShapesContext.Provider>
	);
}

/**
 * Stores the repaired user menu data, in a variety of shapes, and makes it available to child components.
 * This component exists so that it is easy for child components to read and update the user menu data independently of the user side nav data.
 */
function UserMenuDataShapesProviderOLD({
	children,
	rawUserMenuData,
	setUserMenuData,
}: UserMenuDataShapesProviderProps) {
	const { disableMenuCustomization } = useOptions();
	const { allItems, shortcuts } = useItemsAndShortcuts();

	const userMenuDataShapes = useMemo(
		(): UserMenuDataShapes =>
			buildUserMenuDataShapesOLD(
				Object.keys(allItems),
				Object.keys(shortcuts ?? {}),
				// All menu items are visible when there is no user menu data
				disableMenuCustomization ? [] : rawUserMenuData,
				setUserMenuData,
			),
		[allItems, disableMenuCustomization, rawUserMenuData, setUserMenuData, shortcuts],
	);

	return (
		<UserMenuDataShapesContext.Provider value={userMenuDataShapes}>
			{children}
		</UserMenuDataShapesContext.Provider>
	);
}

export function useUserMenuDataShapes() {
	const context = useContext(UserMenuDataShapesContext);

	invariant(
		context,
		'useUserMenuDataShapes must be used inside an initialized UserMenuDataShapesProvider',
	);

	return context;
}
